/**
 * LiveKit sync for raise-hand queue.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useMaybeRoomContext } from "@livekit/components-react";
import { RoomEvent } from "livekit-client";
import {
  LIVE_HAND_TYPE,
  type HandRaiseEntry,
  type LiveHandMessage,
} from "./liveHandTypes";

function encode(msg: object): Uint8Array {
  return new TextEncoder().encode(JSON.stringify(msg));
}

function decode(data: Uint8Array): Record<string, unknown> | null {
  try {
    return JSON.parse(new TextDecoder().decode(data)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export type LiveHandRaisesApi = ReturnType<typeof useLiveHandRaises>;

export function useLiveHandRaises(options: {
  enabled?: boolean;
  localIdentity: string;
  localName: string;
  /** Fired when a student newly raises (not on lower / duplicate). */
  onHandRaised?: (entry: HandRaiseEntry) => void;
}) {
  const room = useMaybeRoomContext();
  const [raisedHands, setRaisedHands] = useState<HandRaiseEntry[]>([]);
  const [myHandRaised, setMyHandRaised] = useState(false);
  const onHandRaisedRef = useRef(options.onHandRaised);
  onHandRaisedRef.current = options.onHandRaised;

  useEffect(() => {
    if (!options.enabled || !room) return;

    const onData = (payload: Uint8Array) => {
      const raw = decode(payload);
      if (!raw || raw.v !== 1 || raw.type !== LIVE_HAND_TYPE) return;
      const sid = String(raw.student_id || "");
      const name = String(raw.student_name || "Student");
      const raised = Boolean(raw.raised);
      const at = Number(raw.at || Date.now());
      if (!sid) return;

      if (sid === options.localIdentity) {
        setMyHandRaised(raised);
      }

      setRaisedHands((prev) => {
        const alreadyRaised = prev.some((h) => h.student_id === sid);
        const rest = prev.filter((h) => h.student_id !== sid);
        if (!raised) return rest;
        const entry: HandRaiseEntry = {
          student_id: sid,
          student_name: name,
          raised_at: at,
        };
        if (!alreadyRaised && sid !== options.localIdentity) {
          queueMicrotask(() => onHandRaisedRef.current?.(entry));
        }
        return [...rest, entry].sort((a, b) => a.raised_at - b.raised_at);
      });
    };

    room.on(RoomEvent.DataReceived, onData);
    return () => {
      room.off(RoomEvent.DataReceived, onData);
    };
  }, [options.enabled, room, options.localIdentity]);

  const setHandRaised = useCallback(
    async (raised: boolean) => {
      if (!room?.localParticipant) return;
      setMyHandRaised(raised);
      setRaisedHands((prev) => {
        const rest = prev.filter((h) => h.student_id !== options.localIdentity);
        if (!raised) return rest;
        return [
          ...rest,
          {
            student_id: options.localIdentity,
            student_name: options.localName || "Student",
            raised_at: Date.now(),
          },
        ];
      });
      const msg: LiveHandMessage = {
        type: LIVE_HAND_TYPE,
        v: 1,
        student_id: options.localIdentity,
        student_name: options.localName || "Student",
        raised,
        at: Date.now(),
      };
      await room.localParticipant.publishData(encode(msg), { reliable: true });
    },
    [room, options.localIdentity, options.localName],
  );

  const lowerHand = useCallback(
    async (studentId: string, studentName: string) => {
      if (!room?.localParticipant) return;
      setRaisedHands((prev) => prev.filter((h) => h.student_id !== studentId));
      if (studentId === options.localIdentity) setMyHandRaised(false);
      const msg: LiveHandMessage = {
        type: LIVE_HAND_TYPE,
        v: 1,
        student_id: studentId,
        student_name: studentName,
        raised: false,
        at: Date.now(),
      };
      await room.localParticipant.publishData(encode(msg), { reliable: true });
    },
    [room, options.localIdentity],
  );

  const toggleMyHand = useCallback(async () => {
    await setHandRaised(!myHandRaised);
  }, [myHandRaised, setHandRaised]);

  return {
    raisedHands,
    myHandRaised,
    setHandRaised,
    toggleMyHand,
    lowerHand,
  };
}
